/**
 * PhotoManager Component
 * Admin interface for managing all photos in the system
 * Features: grid view, bulk selection, bulk delete, filtering, move to album
 *
 * @module components/admin/PhotoManager
 */

import { useState, useEffect, useMemo } from 'react';
import { Photo, Album } from '@/types';
import { logger } from '@/utils/logger';
import Button from '@/components/common/Button';

// ============================================================================
// TYPES
// ============================================================================

interface PhotoManagerProps {
  photos: Photo[];
  albums: Album[];
  isLoading?: boolean;
  onDeletePhotos?: (photoIds: string[]) => Promise<void>;
  onMovePhotos?: (photoIds: string[], albumId: string) => Promise<void>;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  selectedAlbumFilter?: string;
  onAlbumFilterChange?: (albumId: string) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Photo manager component for admin photo management
 * Provides bulk selection, filtering, and batch operations
 *
 * @component
 * @example
 * <PhotoManager
 *   photos={photos}
 *   albums={albums}
 *   onDeletePhotos={handleDelete}
 * />
 */
const PhotoManager: React.FC<PhotoManagerProps> = ({
  photos,
  albums,
  isLoading = false,
  onDeletePhotos,
  onMovePhotos,
  searchQuery = '',
  onSearchChange,
  selectedAlbumFilter = '',
  onAlbumFilterChange,
}) => {
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  // Debug logging
  useEffect(() => {
    logger.debug('[WALRUS] PhotoManager: rendered', {
      photoCount: photos.length,
      selectedCount: selectedPhotoIds.size,
      isLoading,
    });
  }, [photos.length, selectedPhotoIds.size, isLoading]);

  /**
   * Filter and search photos
   */
  const filteredPhotos = useMemo(() => {
    let filtered = photos;

    // Apply album filter
    if (selectedAlbumFilter && selectedAlbumFilter !== 'all') {
      filtered = filtered.filter((p) => p.albumId === selectedAlbumFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.filename.toLowerCase().includes(query) ||
          (p.caption && p.caption.toLowerCase().includes(query)) ||
          (p.uploadedByName && p.uploadedByName.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [photos, selectedAlbumFilter, searchQuery]);

  /**
   * Toggle photo selection
   */
  const togglePhotoSelection = (photoId: string) => {
    logger.debug('[WALRUS] PhotoManager: toggling photo selection', { photoId });

    const newSelected = new Set(selectedPhotoIds);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotoIds(newSelected);
  };

  /**
   * Select all visible photos
   */
  const selectAllPhotos = () => {
    logger.debug('[WALRUS] PhotoManager: selecting all photos', { count: filteredPhotos.length });
    setSelectedPhotoIds(new Set(filteredPhotos.map((p) => p.id)));
  };

  /**
   * Clear selection
   */
  const clearSelection = () => {
    logger.debug('[WALRUS] PhotoManager: clearing selection');
    setSelectedPhotoIds(new Set());
  };

  /**
   * Handle bulk delete
   */
  const handleBulkDelete = async () => {
    if (!onDeletePhotos || selectedPhotoIds.size === 0) return;

    const confirmed = confirm(
      `Are you sure you want to delete ${selectedPhotoIds.size} photo(s)? This cannot be undone.`
    );

    if (!confirmed) {
      logger.debug('[WALRUS] PhotoManager: bulk delete cancelled');
      return;
    }

    try {
      logger.debug('[WALRUS] PhotoManager: starting bulk delete', { count: selectedPhotoIds.size });
      setIsDeleting(true);
      await onDeletePhotos(Array.from(selectedPhotoIds));
      logger.info('[WALRUS] PhotoManager: bulk delete successful');
      setSelectedPhotoIds(new Set());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] PhotoManager: bulk delete failed', { error: errorMessage });
      alert(`Failed to delete photos: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle bulk move to album
   */
  const handleBulkMove = async (albumId: string) => {
    if (!onMovePhotos || selectedPhotoIds.size === 0) return;

    try {
      logger.debug('[WALRUS] PhotoManager: starting bulk move', {
        photoCount: selectedPhotoIds.size,
        albumId,
      });
      setIsMoving(true);
      await onMovePhotos(Array.from(selectedPhotoIds), albumId);
      logger.info('[WALRUS] PhotoManager: bulk move successful');
      setSelectedPhotoIds(new Set());
      setShowMoveMenu(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] PhotoManager: bulk move failed', { error: errorMessage });
      alert(`Failed to move photos: ${errorMessage}`);
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters and search */}
      <div className="space-y-3">
        {/* Search bar */}
        {onSearchChange && (
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-walrus-silver"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search photos by filename, caption, or uploader..."
              value={searchQuery}
              onChange={(e) => {
                logger.debug('[WALRUS] PhotoManager: search changed', { query: e.target.value });
                onSearchChange(e.target.value);
              }}
              className="w-full rounded-lg border border-walrus-gray bg-walrus-charcoal pl-10 pr-4 py-2 text-walrus-white placeholder-walrus-silver transition-colors focus:border-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-20"
            />
          </div>
        )}

        {/* Album filter */}
        {onAlbumFilterChange && (
          <select
            value={selectedAlbumFilter}
            onChange={(e) => {
              logger.debug('[WALRUS] PhotoManager: album filter changed', {
                albumId: e.target.value,
              });
              onAlbumFilterChange(e.target.value);
            }}
            className="rounded-lg border border-walrus-gray bg-walrus-charcoal px-4 py-2 text-walrus-white transition-colors focus:border-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-walrus-charcoal focus:ring-white"
          >
            <option value="">All Albums</option>
            {albums.map((album) => (
              <option key={album.id} value={album.id}>
                {album.name} ({album.photoCount})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Bulk action toolbar */}
      {selectedPhotoIds.size > 0 && (
        <div className="rounded-lg border border-walrus-gray bg-walrus-charcoal/50 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="text-sm text-walrus-white">
              <span className="font-medium">{selectedPhotoIds.size}</span> photo(s) selected
            </div>

            <div className="flex gap-3 flex-wrap">
              {/* Select/Clear buttons */}
              {selectedPhotoIds.size < filteredPhotos.length && (
                <Button variant="ghost" size="sm" onClick={selectAllPhotos}>
                  Select All
                </Button>
              )}

              <Button variant="ghost" size="sm" onClick={clearSelection}>
                Clear
              </Button>

              {/* Move button */}
              {onMovePhotos && (
                <div className="relative">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      logger.debug('[WALRUS] PhotoManager: move menu toggled');
                      setShowMoveMenu(!showMoveMenu);
                    }}
                    disabled={isMoving}
                  >
                    Move {selectedPhotoIds.size > 1 ? '(' + selectedPhotoIds.size + ')' : ''}
                  </Button>

                  {/* Move menu */}
                  {showMoveMenu && (
                    <div className="absolute top-full right-0 mt-2 w-48 rounded-lg border border-walrus-gray bg-walrus-charcoal shadow-lg z-10">
                      <div className="p-2">
                        {albums.length === 0 ? (
                          <p className="text-sm text-walrus-silver p-2 text-center">No albums available</p>
                        ) : (
                          albums.map((album) => (
                            <button
                              key={album.id}
                              onClick={() => handleBulkMove(album.id)}
                              disabled={isMoving}
                              className="w-full text-left px-3 py-2 rounded transition-colors hover:bg-walrus-gray disabled:opacity-50 disabled:cursor-not-allowed text-walrus-white text-sm"
                            >
                              {album.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Delete button */}
              {onDeletePhotos && (
                <Button
                  variant="danger"
                  size="sm"
                  loading={isDeleting}
                  onClick={handleBulkDelete}
                >
                  Delete All
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photos grid */}
      <div>
        <div className="mb-3 text-sm text-walrus-silver">
          {filteredPhotos.length === 0
            ? 'No photos found'
            : `${filteredPhotos.length} photo(s) found`}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {isLoading
            ? Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="rounded-lg overflow-hidden bg-walrus-charcoal aspect-square animate-pulse"
                >
                  <div className="h-full bg-walrus-gray/20"></div>
                </div>
              ))
            : filteredPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className={`relative rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedPhotoIds.has(photo.id)
                      ? 'ring-2 ring-white shadow-lg shadow-white/20'
                      : 'hover:shadow-lg hover:shadow-walrus-gray/50'
                  }`}
                >
                  {/* Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedPhotoIds.has(photo.id)}
                      onChange={() => togglePhotoSelection(photo.id)}
                      className="rounded h-5 w-5 accent-white cursor-pointer"
                      aria-label="Select photo"
                    />
                  </div>

                  {/* Photo image */}
                  <div
                    className="aspect-square bg-walrus-gray overflow-hidden group"
                    onClick={() => togglePhotoSelection(photo.id)}
                  >
                    {photo.thumbnailUrl || photo.url ? (
                      <>
                        <img
                          src={photo.thumbnailUrl || photo.url}
                          alt={photo.filename}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-20" />
                      </>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-walrus-gray">
                        <svg
                          className="h-12 w-12 text-walrus-silver"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Photo info */}
                  <div className="p-3 bg-walrus-charcoal">
                    <h3 className="truncate font-medium text-walrus-white text-sm" title={photo.filename}>
                      {photo.filename}
                    </h3>
                    <p className="text-xs text-walrus-silver mt-1">
                      {photo.uploadedByName || 'Unknown'}
                    </p>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

PhotoManager.displayName = 'PhotoManager';

export default PhotoManager;
